// src/components/OrderList.js
import React, { useState, useEffect } from 'react';
import { Container, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import OrderTable from './OrderTable';
import Pagination from '../Global/Pagination';
import OrderListByCarrier from './OrderListByCarrier';
import TotalCount from '../Global/TotalCount';
import {
  getOrders,
  getOrderAndRelatedById,
  getOrderById,
  logError,
  handleSaveEditOrder,
  getTimeSlots,
  getClientOrigins,
  getPaymentMethods
} from '../../api';
import { formatDateToYYYYMMDD } from '../../utils/DateUtils';
import OrderListFilter from './OrderListFilter';
import Calendar from './Calendar';
import { useSorting } from '../../hooks/useSorting';
import { isComercial } from '../../services/AuthService';
import useFilterPersistence from '../../hooks/useFilterPersistence';
import '../Global/Style/OrderList.css';

const OrderList = () => {
  const [tableKey, setTableKey] = useState(0);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPvp, setTotalPvp] = useState(null);
  const [showTotalPvp, setShowTotalPvp] = useState(false);
  const [openClients, setOpenClients] = useState({});
  const [openOrderItems, setOpenOrderItems] = useState({});
  const [mileage, setMileage] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [timeSlots, setTimeSlots] = useState([]);
  const [clientOrigins, setClientOrigins] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const type = location.pathname.includes('related')
    ? 'related'
    : location.pathname.includes('transport')
    ? 'transport'
    : location.pathname.includes('ordersByCarrier')
    ? 'byCarrier'
    : location.pathname.includes('calendar')
    ? 'calendar'
    : 'orders';

  const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting(
    type === 'transport' || type === 'byCarrier'|| type === 'calendar' ? 'carrierId' : '', 
    false
  );

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDateToYYYYMMDD(tomorrow);
  };

  const getTodayDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(today);
  };

  const getDefaultFilters = () => {
    const base = {
      orderDateFrom: '',
      orderDateTo: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      salesRepId: [],
      carrierId: [],
      incidentTypeId: '',
      incidentActionId: '',
      clientId: '',
      hasSpecialProducts: null,
      zipCode: '',
      city: '',
      clientOriginId: '',
      paymentMethodId: '',
      isImportant: null,
    };
    if (type === 'transport' || type === 'byCarrier') {
      const tomorrow = getTomorrowDate();
      return { ...base, deliveryDateFrom: tomorrow, deliveryDateTo: tomorrow };
    }
    if (type === 'calendar') {
      const today = getTodayDate();
      return { ...base, deliveryDateFrom: today, deliveryDateTo: today };
    }
    return base;
  };

  const [filters, setFilters] = useFilterPersistence(`filters_orders_${type}`, getDefaultFilters());

  const [pagination, setPagination] = useFilterPersistence(`pagination_orders_${type}`, { page: 1, pageSize: undefined });
  const currentPage = pagination.page;
  const productsPerPage = pagination.pageSize;
  const setCurrentPage = (page) => setPagination({ ...pagination, page });
  const setProductsPerPage = (pageSize) => setPagination({ page: 1, pageSize });

  const fetchOrders = async (openItemIds = []) => {
    setLoading(true);
    try {
      const filterParams = {
        ...filters,
        page: currentPage,
        pageSize: productsPerPage,
      };

      let response;

      if (type === 'related' && id) {
        response = await getOrderAndRelatedById(id, filterParams);
      } else if (type === 'transport' || type === 'byCarrier'|| type === 'calendar') {
        response = await getOrders(filterParams, filterParams.page, filterParams.pageSize, orderBy, orderDesc);
      } else {
        response = await getOrders(filterParams, filterParams.page, filterParams.pageSize, orderBy, orderDesc);
      }

      if (response) {
        if (openItemIds.length > 0) {
          const reloaded = await Promise.all(openItemIds.map(oid => getOrderById(Number(oid))));
          setOrders(response.items.map(order => {
            const fresh = reloaded.find(r => r.id === order.id);
            return fresh ? { ...order, items: fresh.items, specialProducts: fresh.specialProducts } : order;
          }));
        } else {
          setOrders(response.items);
        }
        setTotalCount(response.totalCount);
        setTotalPvp(response.totalPvp ?? null);
      }
    } catch (error) {
      logError(error);
      console.error('Error fetching orders:', error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadAuxiliaryData = async () => {
      try {
        const promises = [
          getTimeSlots(),
          getClientOrigins(),
          getPaymentMethods()
        ];
        
        // Solo cargar carriers si el usuario tiene permisos
        const { isComercialAndNotOwnerAdminGestion, isTransportistaAndNotOwnerAdminGestion, isJefeTransportistas } = await import('../../services/AuthService');
        if (!isComercialAndNotOwnerAdminGestion() && (!isTransportistaAndNotOwnerAdminGestion() || isJefeTransportistas())) {
          const { getCarriers } = await import('../../api');
          promises.push(getCarriers());
        }
        
        const results = await Promise.all(promises);
        setTimeSlots(results[0]);
        setClientOrigins(results[1]);
        setPaymentMethods(results[2]);
        if (results[3]) setCarriers(results[3]);
        setIsInitialLoad(false);
      } catch (err) {
        console.error("Error cargando datos:", err);
      }
    };
    
    loadAuxiliaryData();
  }, []);

  useEffect(() => {
    if (!isInitialLoad) {
      fetchOrders();
    }
  }, [id, type, currentPage, productsPerPage, filters, orderBy, orderDesc, isInitialLoad]);

  const handlePageChange = (pageNumber) => {
    setPagination({ ...pagination, page: pageNumber });
  };

  const handleItemsPerPageChange = (itemsPerPage) => {
    setPagination({ page: 1, pageSize: itemsPerPage });
  };

  const handleSaveEdit = async (orderId, modifiedItems, prepaidAmount) => {
    try {
      await handleSaveEditOrder(orderId, modifiedItems, mileage, setSuccessMessage, prepaidAmount);
      const openIds = Object.keys(openOrderItems).filter(oid => openOrderItems[oid]);
      // Incluir siempre el pedido editado aunque no esté en openOrderItems
      const idsToReload = [...new Set([...openIds, String(orderId)])];
      await fetchOrders(idsToReload);
    } catch (error) {
      console.error('Error al guardar los cambios:', error);
      alert('Hubo un problema al guardar los cambios. Inténtelo de nuevo.');
    }
  };

  const reloadPage = () => {
    setTableKey((prevKey) => prevKey + 1);
  };

  const handleFiltersChange = (newFilters) => {
    setPagination({ ...pagination, page: 1 });
    setFilters(newFilters);
  };


  const handleClientToggle = (orderId) => {
    setOpenClients((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleOrderItemToggle = (orderId) => {
    setOpenOrderItems((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleOrderMileageChange = (mileage) => {
    setMileage(mileage);
  };

  const handleUpdateOrderImportance = async (orderId, isImportant) => {
    try {
      const { updateOrder } = await import('../../api');
      await updateOrder(orderId, { id: orderId, isImportant });
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, isImportant }
            : order
        )
      );
    } catch (error) {
      console.error('Error updating order importance:', error);
      throw error;
    }
  };

  return (
    <Container fluid className="my-4 orders-page" data-testid="order-list-container">
      <div>
        {successMessage && (
          <div className="alert alert-success" role="alert" data-testid="success-message">
            {successMessage}
          </div>
        )}
      </div>

      <Row className="mb-4">
        <Col xs={12} md={6}>
          <h1 data-testid="page-title">
            {type === 'related'
              ? 'Pedidos relacionados'
              : type === 'transport'
              ? 'Asignación de Transportista'
              : type === 'byCarrier'
              ? 'Pedidos agrupados por transportista'
              : type === 'calendar'
              ? 'Calendario'
              : 'Pedidos'}
          </h1>
        </Col>
        <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
          {!id && type === 'orders' && isComercial() && (
            <Button
              variant="primary"
              onClick={() => navigate('/orders/new')}
              data-testid="new-order-button"
            >
              <i className="bi bi-plus-circle me-2"></i>Nuevo pedido
            </Button>
          )}
        </Col>
      </Row>

      {selectedClient && (
        <div className="mb-3" data-testid="selected-client-info">
          <strong>Cliente seleccionado:</strong> {selectedClient.name} (ID: {selectedClient.id})
        </div>
      )}

      {type !== 'related' && (
        <OrderListFilter 
          filters={filters} 
          setFilters={handleFiltersChange} 
          type={type}
          clientOrigins={clientOrigins}
          paymentMethods={paymentMethods}
          carriers={carriers}
        />
      )}

      {!loading && type !== 'byCarrier' && type !== 'calendar' && (
        <div className="d-flex justify-content-end mb-2">
          <TotalCount totalCount={totalCount} />
        </div>
      )}

      {type === 'byCarrier' ? (
        <>
          {!loading && totalPvp != null && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowTotalPvp(!showTotalPvp)}
                className="mb-2"
              >
                <i className={`bi bi-eye${showTotalPvp ? '-slash' : ''} me-1`}></i>
                {showTotalPvp ? 'Ocultar' : 'Mostrar'} Total PVP
              </Button>
              {showTotalPvp && (
                <div style={{
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <span style={{ color: '#004085', fontSize: '14px' }}>Total PVP: </span>
                  <span style={{ fontWeight: '600', fontSize: '16px', color: '#004085' }}>{totalPvp.toFixed(2)}€</span>
                </div>
              )}
            </>
          )}
          <OrderListByCarrier orders={orders} loading={loading} data-testid="orders-by-carrier" />
        </>
      ) : type === 'calendar' ? (
        <>
          {!loading && totalPvp != null && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowTotalPvp(!showTotalPvp)}
                className="mb-2"
              >
                <i className={`bi bi-eye${showTotalPvp ? '-slash' : ''} me-1`}></i>
                {showTotalPvp ? 'Ocultar' : 'Mostrar'} Total PVP
              </Button>
              {showTotalPvp && (
                <div style={{
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <span style={{ color: '#004085', fontSize: '14px' }}>Total PVP: </span>
                  <span style={{ fontWeight: '600', fontSize: '16px', color: '#004085' }}>{totalPvp.toFixed(2)}€</span>
                </div>
              )}
            </>
          )}
          <Calendar orders={orders} loading={loading} onDateChange={handleFiltersChange} filters={filters} data-testid="orders-calendar" />
        </>
      ) : (
        <>
          {!loading && totalPvp != null && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowTotalPvp(!showTotalPvp)}
                className="mb-2"
              >
                <i className={`bi bi-eye${showTotalPvp ? '-slash' : ''} me-1`}></i>
                {showTotalPvp ? 'Ocultar' : 'Mostrar'} Total PVP
              </Button>
              {showTotalPvp && (
                <div style={{
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}>
                  <span style={{ color: '#004085', fontSize: '14px' }}>Total PVP: </span>
                  <span style={{ fontWeight: '600', fontSize: '16px', color: '#004085' }}>{totalPvp.toFixed(2)}€</span>
                </div>
              )}
            </>
          )}
          <OrderTable
            key={tableKey}
            orders={loading ? null : orders}
            setOrders={setOrders}
            totalCount={totalCount}
            currentPage={currentPage}
            productsPerPage={productsPerPage}
            setCurrentPage={setCurrentPage}
            setProductsPerPage={setProductsPerPage}
            openClients={openClients}
            openOrderItems={openOrderItems}
            handleClientToggle={handleClientToggle}
            handleOrderItemToggle={handleOrderItemToggle}
            handleSaveEdit={handleSaveEdit}
            handleOrderMileageChange={handleOrderMileageChange}
            reloadPage={reloadPage}
            onClientSaved={fetchOrders}
            timeSlots={timeSlots}
            clientOrigins={clientOrigins}
            paymentMethods={paymentMethods}
            onSort={handleSort}
            getSortIcon={getSortIcon}
            onUpdateOrderImportance={handleUpdateOrderImportance}
          />
        </>
      )}

      <div className="d-flex justify-content-center mt-4" data-testid="pagination-container">
        <Pagination
          totalCount={totalCount}
          currentPage={currentPage}
          itemsPerPage={productsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </Container>
  );
};

export default OrderList;
